import React, { createRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EmojiSmile, Send } from 'react-bootstrap-icons';

import { Button, Form, message, Popover, Tag, Typography } from 'antd';

import emojis from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';

import { stripEmojis } from '@/helpers/characters';
import useThemeMode from '@/hooks/useThemeMode';
import styled, { css, StyledProp } from '@/styled';

import { Mention } from '../ListComment/Actions';

export type CommentFormValues = { message: string };

interface IProps extends StyledProp {
  onFinish: (values: CommentFormValues) => Promise<any>;
  isLoading: boolean;
  mention?: Mention;
  removeMention?: () => void;
}

const CommentForm = (props: IProps) => {
  const [form] = Form.useForm();
  const input = useRef<HTMLDivElement>();
  const mentionRef = createRef<HTMLDivElement>();

  const [mentionWidth, setMentionWidth] = useState(0);

  const formMessageValue = Form.useWatch('message', form);
  const { isDark } = useThemeMode();

  const getRealMessageLength = (message: string) => {
    const { value, count } = stripEmojis(message);
    return value.length + count;
  };

  useEffect(() => {
    input.current.focus();
  }, [input]);

  useLayoutEffect(() => {
    setMentionWidth(mentionRef.current.offsetWidth);
  }, [mentionRef, setMentionWidth]);

  const handleInputChange = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const caretPos = window.getSelection().getRangeAt(0).startOffset;

    if (!event.shiftKey && event.key == 'Enter') {
      event.preventDefault();
      form.submit();
    }

    if (event.key == 'Backspace') {
      (event.currentTarget.textContent === '' || caretPos == 0) && props.removeMention();
    }
  };

  const setEmoji = (emoji: { native: string }) => {
    const currentText = input.current.innerText;
    const text = currentText + emoji.native;
    input.current.innerText = text;

    form.setFieldsValue({ message: text ?? '' });
  };

  const formatMessageWithMention = (message: string) => `@${props.mention.id} ${message}`;

  const handleUpdateForm = (event: React.KeyboardEvent<HTMLDivElement>) => {
    form.setFieldValue('message', event.currentTarget.innerText);
  };

  const handleOnFinish = (values: CommentFormValues) => {
    if (props.mention) {
      values.message = formatMessageWithMention(values.message);
    }

    props
      .onFinish(values)
      .then(() => {
        form.resetFields();
        message.success('Comentário enviado com sucesso');
      })
      .catch(() => {
        message.error('Ocorreu um erro ao enviar o comentário');
      });
  };

  return (
    <Form form={form} onFinish={handleOnFinish} className={props.className}>
      <div className='comments-input-container'>
        <div ref={mentionRef} className='comment-input-prefix-container'>
          {props.mention && <Tag className='reply-mention-tag' color={'primary'}>{`@${props.mention.name}`}</Tag>}
        </div>
        <Form.Item
          className='comments-input-field'
          name='message'
          rules={[
            { required: true },
            {
              validator(_, comment) {
                const realValueLength = getRealMessageLength(comment);
                const maxLength = 500;
                if (realValueLength <= maxLength) return Promise.resolve();
                return Promise.reject('Escreva no máximo 500 caracteres');
              }
            }
          ]}
        >
          <div
            ref={input}
            contentEditable
            style={{ textIndent: props.mention ? mentionWidth - 8 : 'initial' }}
            id='comments-input'
            onKeyDown={handleInputChange}
            onKeyUp={handleUpdateForm}
            className='comments-input'
            placeholder='Escreva uma resposta'
          />
        </Form.Item>

        <div className='comments-input-suffix-container' onMouseDown={e => e.preventDefault()}>
          <Popover
            placement='bottom'
            overlayInnerStyle={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
            content={
              <EmojiPicker
                emojiSize={24}
                perLine={7}
                data={emojis}
                onEmojiSelect={setEmoji}
                locale='pt'
                theme={isDark ? 'dark' : 'light'}
              />
            }
            trigger='click'
          >
            <Button id='comments-add-emoji-button' className='comments-button'>
              <EmojiSmile data-disabled={props.isLoading} />
            </Button>
          </Popover>
          <Button
            htmlType='submit'
            loading={props.isLoading}
            id='comments-send-message-button'
            className='comments-button'
            icon={<Send data-visible={!props.isLoading} />}
          />
        </div>
      </div>

      <Typography.Text>{getRealMessageLength(formMessageValue) ?? 0}/500</Typography.Text>
    </Form>
  );
};

export default styled(CommentForm)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .comments-popover-container {
      padding: 0;
      margin: 0;
    }

    .comments-input-container {
      position: relative;
      width: 100%;

      & .ant-form-item {
        margin-bottom: 4px;
      }
      .comments-input-field {
        margin-bottom: 0.5rem;
      }

      .comments-input-suffix-container {
        position: absolute;
        top: 0;
        z-index: 3;
        right: ${theme.space.pxToRem(12)};
        height: 40px;

        display: flex;
        gap: ${theme.space.pxToRem(8)};
        align-items: center;
        justify-content: center;
        svg {
          font-size: 16;
        }
      }

      .comment-input-prefix-container {
        position: absolute;
        z-index: 4;
        top: 0;
        bottom: 0;
        display: flex;
        margin-left: 8px;
        margin-top: 8px;
        display: flex;
        align-items: flex-start;
        justify-content: center;
      }

      .comments-input {
        width: 100%;
        overflow: hidden;
        max-height: unset;
        height: 100%;
        background: ${theme.variables.inputColor};
        border: 1px solid ${theme.variables.inputColorBorder};
        border-radius: 4px;
        min-height: auto !important;
        padding: ${theme.space.spacing(2, 14, 2, 3)};
        overflow: hidden;
        outline: none;
        &:focus,
        :focus-visible {
          transition: 0.2s;
          border-color: ${theme.colorPrimary};
        }
      }
    }

    .comments-button {
      all: unset;
      width: 16px;
      height: 16px;
      cursor: pointer;
    }

    input {
      resize: vertical;
      padding: 2px;
    }

    .reply-mention-tag {
      color: ${theme.colorText};
    }

    button {
      display: flex;
    }

    #comments-send-message-button {
      span {
        display: flex;
        font-size: 12px;
      }
    }

    .ant-form-item-explain-error {
      position: absolute;
    }
  `
);
